import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "./store/authStore";
import ErrorBoundary from "./components/common/ErrorBoundary";
import PlanLimitModal from "./components/common/PlanLimitModal";

// Layouts (Loaded eagerly for layout shell stability)
import AuthLayout from "./components/layout/AuthLayout";
import DashboardLayout from "./components/layout/DashboardLayout";
import SuperAdminLayout from "./components/layout/SuperAdminLayout";

// Route Guards
import ProtectedRoute from "./routes/ProtectedRoute";
import SuperAdminGuard from "./routes/SuperAdminGuard";

// Lazy-loaded Pages (Code-split for maximum performance)
// Auth Pages
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("./pages/auth/RegisterPage"));
const VerifyEmailPage = lazy(() => import("./pages/auth/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("./pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));
const AcceptInvitePage = lazy(() => import("./pages/auth/AcceptInvitePage"));

// Dashboard & Core
const DashboardPage = lazy(() => import("./pages/dashboard/DashboardPage"));

// Clients
const ClientsPage = lazy(() => import("./pages/clients/ClientsPage"));
const ClientDetailPage = lazy(() => import("./pages/clients/ClientDetailPage"));
const SegmentsPage = lazy(() => import("./pages/clients/SegmentsPage"));

// Plans & Recoveries
const PlansPage = lazy(() => import("./pages/plans/PlansPage"));
const DebtRecoveryPage = lazy(() => import("./pages/plans/DebtRecoveryPage"));
const DebtRecoveryDetailPage = lazy(() => import("./pages/plans/DebtRecoveryDetailPage"));
const CollectorQueuePage = lazy(() => import("./pages/plans/CollectorQueuePage"));

// Messaging & Automations
const InboxPage = lazy(() => import("./pages/messaging/InboxPage"));
const CampaignsPage = lazy(() => import("./pages/messaging/CampaignsPage"));
const CampaignDetailPage = lazy(() => import("./pages/messaging/CampaignDetailPage"));
const AutomationsPage = lazy(() => import("./pages/messaging/AutomationsPage"));
const AutomationDetailPage = lazy(() => import("./pages/messaging/AutomationDetailPage"));
const TemplatesPage = lazy(() => import("./pages/messaging/TemplatesPage"));

// Payments & Billing
const PaymentsPage = lazy(() => import("./pages/payments/PaymentsPage"));
const PaymentLinkDetailPage = lazy(() => import("./pages/payments/PaymentLinkDetailPage"));
const TransactionDetailPage = lazy(() => import("./pages/payments/TransactionDetailPage"));
const BillingPage = lazy(() => import("./pages/payments/BillingPage"));
const PublicPaymentCallbackPage = lazy(() => import("./pages/payments/PublicPaymentCallbackPage"));

// Portal & Settings
const BorrowerPortalPage = lazy(() => import("./pages/portal/BorrowerPortalPage"));
const SettingsPage = lazy(() => import("./pages/settings/SettingsPage"));
const CompliancePage = lazy(() => import("./pages/settings/CompliancePage"));
const ManageOrganizationsPage = lazy(() => import("./pages/organisations/ManageOrganizationsPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));

// BRM / Relationship Managers
const BrmApplyPage = lazy(() => import("./pages/brm/BrmApplyPage"));
const BrmPortalPage = lazy(() => import("./pages/brm/BrmPortalPage"));

// Super Admin
const SuperAdminPage = lazy(() => import("./pages/admin/SuperAdminPage"));
const SuperAdminUserDetail = lazy(() => import("./pages/admin/SuperAdminUserDetail"));
const SuperAdminOrgDetail = lazy(() => import("./pages/admin/SuperAdminOrgDetail"));

// Loading Shell Spinner
function PageLoadingShell() {
  return (
    <div className="flex h-96 w-full items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="h-9 w-9 animate-spin rounded-full border-3 border-indigo-200 border-t-indigo-600" />
        <p className="text-xs font-semibold text-slate-400">Loading module...</p>
      </div>
    </div>
  );
}

function App() {
  const isLoading = useAuthStore((state) => state.isLoading);

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <PlanLimitModal />
      <Suspense fallback={<PageLoadingShell />}>
        <Routes>
          {/* Public Borrower Portal & Pay Routes */}
          <Route path="/portal/:token" element={<BorrowerPortalPage />} />
          <Route path="/pay/:token" element={<BorrowerPortalPage />} />
          <Route path="/apply/brm" element={<BrmApplyPage />} />

          {/* Dedicated BRM Portal Routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/brm" element={<BrmPortalPage />} />
            <Route path="/brm/*" element={<BrmPortalPage />} />
          </Route>

          {/* Public Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route
              path="/payments/callback"
              element={<PublicPaymentCallbackPage />}
            />
          </Route>

          {/* Protected Tenant Workspace Routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<DashboardLayout />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />

              <Route path="/segments" element={<SegmentsPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/clients/:id" element={<ClientDetailPage />} />

              <Route path="/plans" element={<PlansPage />} />
              <Route path="/debt-recoveries" element={<DebtRecoveryPage />} />
              <Route
                path="/debt-recoveries/:id"
                element={<DebtRecoveryDetailPage />}
              />
              <Route path="/collector-queue" element={<CollectorQueuePage />} />

              <Route path="/inbox" element={<InboxPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/message-templates" element={<Navigate to="/templates" replace />} />
              <Route path="/messaging" element={<Navigate to="/campaigns" replace />} />
              <Route path="/campaigns" element={<CampaignsPage />} />
              <Route path="/campaigns/:id" element={<CampaignDetailPage />} />

              <Route path="/automations" element={<AutomationsPage />} />
              <Route path="/automations/:id" element={<AutomationDetailPage />} />

              <Route path="/payments" element={<PaymentsPage />} />
              <Route
                path="/payments/links/:id"
                element={<PaymentLinkDetailPage />}
              />
              <Route
                path="/payments/transactions/:id"
                element={<TransactionDetailPage />}
              />
              <Route path="/billing" element={<BillingPage />} />

              <Route path="/organizations" element={<ManageOrganizationsPage />} />

              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/gateways" element={<Navigate to="/settings?tab=gateways" replace />} />
              <Route path="/settings/payment-methods" element={<Navigate to="/settings?tab=gateways" replace />} />
              <Route path="/settings/payments" element={<Navigate to="/settings?tab=gateways" replace />} />
              <Route path="/compliance" element={<Navigate to="/superadmin/governance" replace />} />
              <Route path="/notifications" element={<NotificationsPage />} />
            </Route>
          </Route>

          {/* Super Admin Routes */}
          <Route element={<SuperAdminLayout />}>
            <Route element={<SuperAdminGuard />}>
              <Route path="/superadmin" element={<SuperAdminPage />} />
              <Route path="/superadmin/users" element={<SuperAdminPage />} />
              <Route
                path="/superadmin/users/:id"
                element={<SuperAdminUserDetail />}
              />
              <Route path="/superadmin/organizations" element={<SuperAdminPage />} />
              <Route
                path="/superadmin/organizations/:id"
                element={<SuperAdminOrgDetail />}
              />
              <Route path="/superadmin/brms" element={<SuperAdminPage />} />
              <Route path="/superadmin/clients" element={<SuperAdminPage />} />
              <Route path="/superadmin/campaigns" element={<SuperAdminPage />} />
              <Route path="/superadmin/automations" element={<SuperAdminPage />} />
              <Route path="/superadmin/payments" element={<SuperAdminPage />} />
              <Route path="/superadmin/ai" element={<SuperAdminPage />} />
              <Route path="/superadmin/system" element={<SuperAdminPage />} />
              <Route path="/superadmin/contacts" element={<SuperAdminPage />} />
              <Route path="/superadmin/waitlist" element={<SuperAdminPage />} />
              <Route path="/superadmin/governance" element={<CompliancePage />} />
            </Route>
          </Route>

          {/* Catch-all: redirect unknown routes */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
